import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaCheck,
  FaMoon,
  FaSearch,
  FaSpinner,
  FaSun,
  FaUtensils,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/axios";
import "./admPatrocinadoresList.css";
import "./adm.css";
import "./incluirReuniaoJantar.css";

const HORARIOS_REFEICAO = [
  { horario: "13:00", tipo: "almoco", label: "Almoço" },
  { horario: "20:00", tipo: "jantar", label: "Jantar" },
];

const IncluirReuniaoJantar = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [patrocinador, setPatrocinador] = useState(null);
  const [diasEvento, setDiasEvento] = useState([]);
  const [dataSelecionada, setDataSelecionada] = useState("");
  const [horarioSelecionado, setHorarioSelecionado] = useState("");
  const [participantes, setParticipantes] = useState([]);
  const [listaMarcado, setListaMarcado] = useState([]);
  const [idsMarcados, setIdsMarcados] = useState([]);
  const [idsMarcadosPatrocinador, setIdsMarcadosPatrocinador] = useState([]);
  const [idsReunioes, setIdsReunioes] = useState([]);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState([]);
  const [nomePesquisa, setNomePesquisa] = useState("");
  const [isLoadingMeta, setIsLoadingMeta] = useState(true);
  const [isLoadingLista, setIsLoadingLista] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const labelDiaAtivo = useMemo(() => {
    const dia = diasEvento.find((d) => d.data === dataSelecionada);
    return dia?.label || "—";
  }, [diasEvento, dataSelecionada]);

  const refeicaoAtiva = useMemo(
    () => HORARIOS_REFEICAO.find((h) => h.horario === horarioSelecionado) || null,
    [horarioSelecionado]
  );

  const idsExcluidos = useMemo(() => {
    return [
      ...new Set([
        ...idsMarcados,
        ...idsMarcadosPatrocinador,
        ...idsReunioes,
      ]),
    ];
  }, [idsMarcados, idsMarcadosPatrocinador, idsReunioes]);

  const podeCriar =
    usuarioSelecionado.length > 0 &&
    Boolean(horarioSelecionado) &&
    Boolean(dataSelecionada) &&
    !isSaving;

  const carregarMeta = useCallback(async () => {
    if (!id) return;
    setIsLoadingMeta(true);
    setError(null);
    try {
      const [respDias, respPatrocinador] = await Promise.all([
        api.get("/agenda-dias-evento"),
        api.get(`/patrocinador/${id}`),
      ]);

      const dias = respDias.data?.dados?.dias || [];
      const patro = respPatrocinador.data?.dados || null;

      setDiasEvento(dias);
      setPatrocinador(patro);
      setDataSelecionada(dias[0]?.data || "");
    } catch (err) {
      console.error("Erro ao carregar metadados:", err);
      setError("Erro ao carregar dias do evento e dados do patrocinador.");
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao carregar dados para marcar almoço/jantar."
      );
    } finally {
      setIsLoadingMeta(false);
    }
  }, [id]);

  const carregarConvidados = useCallback(
    async (nome = "", merge = []) => {
      setIsLoadingLista(true);
      try {
        const respUsuarios = await api.post("/usuarios-jantar", {
          tipo: ["convidado"],
          nome,
          idsNot: merge,
        });
        if (respUsuarios.status === 200) {
          setParticipantes(respUsuarios.data?.dados || []);
        }
      } catch (err) {
        console.error("Erro ao carregar convidados:", err);
        setParticipantes([]);
        toast.error(
          err?.response?.data?.mensagem || "Erro ao carregar convidados."
        );
      } finally {
        setIsLoadingLista(false);
      }
    },
    []
  );

  const carregarMarcados = useCallback(
    async (data, horario) => {
      const idUsuarioPatro =
        patrocinador?.id_usuario ?? patrocinador?.idUsuario;
      if (!idUsuarioPatro || !data || !horario) {
        setListaMarcado([]);
        setIdsMarcados([]);
        setIdsMarcadosPatrocinador([]);
        setIdsReunioes([]);
        return;
      }

      setIsLoadingLista(true);
      try {
        const resp = await api.post("/agenda-lista-jantar", {
          id_patrocinador: idUsuarioPatro,
          data,
          horario,
        });

        if (resp.status === 200) {
          const marcados = resp.data?.idsMarcados || [];
          const marcadosPatro = resp.data?.idsMarcadosPatrocinador || [];
          const reunioes = resp.data?.reunioes || [];
          const idsReuniao = reunioes
            .map((r) => r.participantes?.[0]?.id_usuario ?? r.participantes?.[0]?.idUsuario)
            .filter(Boolean);

          setListaMarcado(resp.data?.listaFiltrada || []);
          setIdsMarcados(marcados);
          setIdsMarcadosPatrocinador(marcadosPatro);
          setIdsReunioes(idsReuniao);

          const merge = [...new Set([...marcados, ...marcadosPatro, ...idsReuniao])];
          await carregarConvidados(nomePesquisa, merge);
          return;
        }
      } catch (err) {
        console.error("Erro ao listar almoço/jantar:", err);
        toast.error(
          err?.response?.data?.mensagem ||
            "Erro ao carregar agendamentos deste horário."
        );
        setListaMarcado([]);
        setParticipantes([]);
      } finally {
        setIsLoadingLista(false);
      }
    },
    [patrocinador, nomePesquisa, carregarConvidados]
  );

  useEffect(() => {
    carregarMeta();
  }, [carregarMeta]);

  useEffect(() => {
    if (isLoadingMeta || !dataSelecionada) return;
    setHorarioSelecionado("");
    setUsuarioSelecionado([]);
    setListaMarcado([]);
    setParticipantes([]);
    setIdsMarcados([]);
    setIdsMarcadosPatrocinador([]);
    setIdsReunioes([]);
  }, [dataSelecionada, isLoadingMeta]);

  const handleClickHorario = async (horario) => {
    setHorarioSelecionado(horario);
    setUsuarioSelecionado([]);
    await carregarMarcados(dataSelecionada, horario);
  };

  const handlePesquisaConvidado = (nome) => {
    setNomePesquisa(nome);
    carregarConvidados(nome, idsExcluidos);
  };

  const toggleConvidado = (participanteId) => {
    setUsuarioSelecionado((prev) => {
      if (prev.includes(participanteId)) {
        return prev.filter((idUser) => idUser !== participanteId);
      }
      return [...prev, participanteId];
    });
  };

  const handleCriarReuniao = async () => {
    const idUsuarioPatro =
      patrocinador?.id_usuario ?? patrocinador?.idUsuario ?? null;

    if (!idUsuarioPatro) {
      toast.error("Patrocinador sem usuário vinculado.");
      return;
    }
    if (!horarioSelecionado) {
      toast.warning("Selecione um horário de almoço ou jantar.");
      return;
    }
    if (!usuarioSelecionado.length) {
      toast.warning("Selecione ao menos um convidado.");
      return;
    }

    setIsSaving(true);
    try {
      const resp = await api.post("/agenda-marcar-jantar", {
        ids_usuario: usuarioSelecionado,
        data: dataSelecionada,
        horario: horarioSelecionado,
        id_patrocinador: idUsuarioPatro,
      });

      if (resp.status === 200) {
        if (resp.data?.status === "erro") {
          toast.error(resp.data?.mensagem || "Não foi possível criar a reunião.");
          return;
        }
        toast.success(
          resp.data?.mensagem || "Reunião de almoço/jantar criada com sucesso!"
        );
        navigate(-1);
      }
    } catch (err) {
      console.error("Erro ao criar almoço/jantar:", err);
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao criar a reunião. Tente novamente."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const nomePatrocinador =
    patrocinador?.patrocinador?.trim() || "Patrocinador";

  return (
    <div className="apl-page apl-page--padded irj-page">
      <header className="apl-header" aria-label="Cabeçalho almoço/jantar">
        <div className="apl-header-text">
          <h1>Almoço / Jantar</h1>
          <p>
            Selecione o dia do evento, o horário de refeição e os convidados para{" "}
            <strong>{nomePatrocinador}</strong>. Almoço às 13:00 e jantar às
            20:00 em todos os dias.
          </p>
        </div>

        <div className="apl-toolbar apl-toolbar--inline">
          <div className="apl-toolbar-actions">
            <button
              type="button"
              className="apl-btn apl-btn--primary"
              onClick={handleCriarReuniao}
              disabled={!podeCriar || isLoadingMeta || isLoadingLista}
            >
              {isSaving ? (
                <FaSpinner className="fa-spin" size={14} aria-hidden />
              ) : (
                <FaUtensils size={14} aria-hidden />
              )}
              {isSaving ? "Criando…" : "Criar reunião"}
            </button>
            <button
              type="button"
              className="apl-btn apl-btn--ghost"
              onClick={() => navigate(-1)}
              disabled={isSaving}
            >
              <FaArrowLeft size={14} aria-hidden />
              Voltar
            </button>
          </div>
        </div>
      </header>

      <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
        {error ? (
          <div className="apl-alert" role="alert">
            {error}
          </div>
        ) : null}

        {!isLoadingMeta && diasEvento.length > 0 ? (
          <nav className="apl-day-tabs" aria-label="Dias do evento">
            {diasEvento.map((item) => (
              <button
                key={item.data}
                type="button"
                className={`apl-day-tab${
                  dataSelecionada === item.data ? " is-active" : ""
                }`}
                onClick={() => setDataSelecionada(item.data)}
                disabled={isLoadingMeta || isSaving}
              >
                {item.label}
              </button>
            ))}
          </nav>
        ) : null}

        {!isLoadingMeta ? (
          <section className="apl-metrics" aria-label="Indicadores">
            <div className="apl-metric apl-metric--total">
              <span className="apl-metric-value">{participantes.length}</span>
              <span className="apl-metric-label">Convidados disponíveis</span>
            </div>
            <div className="apl-metric">
              <span className="apl-metric-value">{labelDiaAtivo}</span>
              <span className="apl-metric-label">Dia selecionado</span>
            </div>
            <div className="apl-metric">
              <span className="apl-metric-value">
                {refeicaoAtiva
                  ? `${refeicaoAtiva.label} ${refeicaoAtiva.horario}`
                  : "—"}
              </span>
              <span className="apl-metric-label">Refeição</span>
            </div>
            <div className="apl-metric">
              <span className="apl-metric-value">{listaMarcado.length}</span>
              <span className="apl-metric-label">Já agendados</span>
            </div>
          </section>
        ) : null}

        {isLoadingMeta ? (
          <div className="apl-card">
            <div className="apl-loading">
              <FaSpinner className="fa-spin" size={28} aria-hidden />
              <span>Carregando dias do evento…</span>
            </div>
          </div>
        ) : diasEvento.length === 0 ? (
          <div className="apl-card">
            <div className="apl-empty">
              <p className="apl-empty-title">Nenhum dia de evento</p>
              <p style={{ margin: 0 }}>
                Cadastre as datas do evento vigente para marcar almoço/jantar.
              </p>
            </div>
          </div>
        ) : (
          <div className="irj-grid">
            <section className="apl-card irj-panel" aria-label="Horários">
              <div className="irj-panel-head">
                <h2 className="irj-panel-title">Horários em {labelDiaAtivo}</h2>
              </div>
              <div className="irj-meal-list" role="listbox" aria-label="Refeições">
                {HORARIOS_REFEICAO.map((item) => {
                  const ativo = horarioSelecionado === item.horario;
                  return (
                    <button
                      key={item.horario}
                      type="button"
                      role="option"
                      aria-selected={ativo}
                      className={`irj-meal-option${ativo ? " is-selected" : ""}`}
                      onClick={() => handleClickHorario(item.horario)}
                      disabled={isSaving}
                    >
                      <span className="irj-meal-icon" aria-hidden>
                        {item.tipo === "almoco" ? (
                          <FaSun size={16} />
                        ) : (
                          <FaMoon size={16} />
                        )}
                      </span>
                      <span className="irj-meal-main">
                        <span className="irj-meal-name">{item.label}</span>
                        <span className="irj-meal-time">{item.horario}</span>
                      </span>
                      {ativo ? (
                        <FaCheck className="irj-guest-check" aria-hidden />
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="apl-card irj-panel" aria-label="Convidados">
              <div className="irj-panel-head">
                <h2 className="irj-panel-title">Convidados</h2>
                <div className="apl-search irj-search">
                  <span className="apl-search-icon" aria-hidden>
                    <FaSearch size={14} />
                  </span>
                  <input
                    type="search"
                    className="apl-search-input"
                    placeholder="Buscar por nome…"
                    value={nomePesquisa}
                    onChange={(e) => handlePesquisaConvidado(e.target.value)}
                    disabled={!horarioSelecionado || isLoadingLista || isSaving}
                    aria-label="Buscar convidado"
                  />
                </div>
              </div>

              {!horarioSelecionado ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">Selecione um horário</p>
                  <p style={{ margin: 0 }}>
                    Escolha almoço ou jantar ao lado para listar os convidados
                    disponíveis.
                  </p>
                </div>
              ) : isLoadingLista ? (
                <div className="apl-loading irj-panel-loading">
                  <FaSpinner className="fa-spin" size={24} aria-hidden />
                  <span>Carregando convidados…</span>
                </div>
              ) : participantes.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">Nenhum convidado encontrado</p>
                  <p style={{ margin: 0 }}>
                    Ajuste a busca ou selecione outro horário.
                  </p>
                </div>
              ) : (
                <>
                  {usuarioSelecionado.length > 0 ? (
                    <p className="irj-selection-hint">
                      {usuarioSelecionado.length}{" "}
                      {usuarioSelecionado.length === 1
                        ? "convidado selecionado"
                        : "convidados selecionados"}
                    </p>
                  ) : null}
                  <ul className="irj-guest-list" aria-label="Lista de convidados">
                    {participantes.map((p) => {
                      const ativo = usuarioSelecionado.includes(p.id);
                      return (
                        <li key={p.id}>
                          <button
                            type="button"
                            className={`irj-guest-item${ativo ? " is-active" : ""}`}
                            onClick={() => toggleConvidado(p.id)}
                            disabled={isSaving}
                          >
                            <div className="irj-guest-main">
                              <span className="irj-guest-name">{p.nome || "—"}</span>
                            </div>
                            {ativo ? (
                              <FaCheck className="irj-guest-check" aria-hidden />
                            ) : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="irj-actions">
                    <button
                      type="button"
                      className="apl-btn apl-btn--primary"
                      onClick={handleCriarReuniao}
                      disabled={!podeCriar}
                    >
                      {isSaving ? (
                        <FaSpinner className="fa-spin" size={14} aria-hidden />
                      ) : (
                        <FaUtensils size={14} aria-hidden />
                      )}
                      {isSaving ? "Criando…" : "Criar reunião"}
                    </button>
                  </div>
                </>
              )}
            </section>

            <section
              className="apl-card irj-panel irj-panel--booked"
              aria-label="Já agendados"
            >
              <div className="irj-panel-head">
                <h2 className="irj-panel-title">Já agendados</h2>
              </div>

              {!horarioSelecionado ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">Sem horário selecionado</p>
                  <p style={{ margin: 0 }}>
                    Os convidados já marcados neste horário aparecerão aqui.
                  </p>
                </div>
              ) : listaMarcado.length === 0 ? (
                <div className="apl-empty">
                  <p className="apl-empty-title">Nenhum agendamento</p>
                  <p style={{ margin: 0 }}>
                    Ainda não há convidados marcados para este horário com este
                    patrocinador.
                  </p>
                </div>
              ) : (
                <ul className="irj-booked-list" aria-label="Convidados agendados">
                  {listaMarcado.map((item) => {
                    const nome =
                      item.participantes?.[0]?.usuario?.nome || "—";
                    const key = item.id || nome;
                    return (
                      <li key={key}>
                        <div className="irj-booked-item">
                          <div className="irj-guest-main">
                            <span className="irj-guest-name">{nome}</span>
                          </div>
                          <span className="irj-booked-badge">Marcado</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncluirReuniaoJantar;
